import React, { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { isPinData } from '../interfaces';
import { getClusters, setIsInitialized } from '../../../store/map/actions';
import { AppState } from '../../../store/interfaces';
import { isRide } from '../../../types/ride';
import { SearchPins } from '../SearchPins';
import { Clusters } from '../Clusters';
import { Layers } from '../Layers';
import { NewPin } from '../NewPin';
import { DriversAround } from '../DriversAround';
import { debounce } from '../../debounce';
import { Search } from '../Search';
import { FEATURE_IS_ALLOWED_OSM } from '../../../config/default-features';
import urls from '../../../config/urls';

const useMarkers = (): void => {
  const bounds: any = [];
  const map = useMap();
  const dispatch = useDispatch();

  const markers = useSelector((state: AppState) => state.map.markers, shallowEqual);
  const clusters = useSelector((state: AppState) => state.map.clusters.list, shallowEqual);
  const resetPosition = useSelector((state: AppState) => state.map.resetPosition);

  useEffect(() => {
    if (!markers.isInitialized) {
      return;
    }

    if (markers.list.length > 0) {
      clusters.forEach((cluster) => bounds.push([+cluster.lat, +cluster.lng]));
      markers.list.forEach((marker) => {
        bounds.push([+marker.lat, +marker.lng]);
        marker.clusters.forEach((cluster) => bounds.push([+cluster.lat, +cluster.lng]));
        marker.drivers.forEach((driver) => bounds.push([+driver.lat, +driver.lng]));
      });
      if (bounds.length === 1) {
        map.setView(bounds[0], 13);
      } else {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      }

      dispatch(setIsInitialized(true));
      return;
    }

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          map.setView([lat, lng], 12); // Зум 12 — город
          dispatch(setIsInitialized(true));
        },
        (err) => {
          map.setView([44.9672433944, -103.7715563417], 4); // Центр США, зум 4
          dispatch(setIsInitialized(true));
        },
        {
          enableHighAccuracy: true,
          timeout: 4000,
          maximumAge: 5000,
        }
      );
    } else {
      map.setView([44.9672433944, -103.7715563417], 4); // Центр США, зум 4
      dispatch(setIsInitialized(true));
    }
  }, [markers, resetPosition]);
};

const getClustersByCoords = debounce((lat, lng, dispatch): void => {
  // dispatch(getClusters.request(lat, lng));
}, 500);

export const MapMarkers: React.FC = React.memo(function () {
  const map = useMap();
  const dispatch = useDispatch();
  const location = useLocation();

  const { selectedObject, resetPosition } = useSelector(
    (app: AppState) => ({
      selectedObject: app.map.selectedObject,
      resetPosition: app.map.resetPosition,
    }),
    shallowEqual
  );

  useEffect(() => {
    map.invalidateSize();
  }, [resetPosition]);

  useEffect(() => {
    map.invalidateSize();

    if (!selectedObject) {
      return;
    }

    if (isRide(selectedObject)) {
      return;
    }

    if (isPinData(selectedObject)) {
      if (selectedObject.isDriverCoords === undefined) {
        getClustersByCoords(selectedObject.position.lat, selectedObject.position.lng, dispatch);
      }

      map.flyTo(selectedObject.position);
      return;
    }

    if (selectedObject.clusters.length === 0) {
      getClustersByCoords(selectedObject.lat, selectedObject.lng, dispatch);
    }

    map.flyTo({ lat: +selectedObject.lat, lng: +selectedObject.lng });
  }, [selectedObject]);

  useMarkers();

  return (
    <>
      {FEATURE_IS_ALLOWED_OSM && <Search />}
      <SearchPins />
      <Clusters />
      <DriversAround />
      {location.pathname.includes(urls.map) && <Layers />}
      <NewPin />
    </>
  );
});
